import React, { useState, useEffect, useCallback } from "react";
import {
  voucherExchangesService,
  VoucherExchange,
} from "../../services/voucherExchanges";
import { apiService, Campaign } from "../../services";
import { partnersService, Partner } from "../../services/partners";
import { authService } from "../../services/auth";

const VoucherExchangeTab: React.FC = () => {
  const canSwitchPartner =
    typeof authService.isAdmin === "function" ? authService.isAdmin() : true;

  const [exchanges, setExchanges] = useState<VoucherExchange[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const [phoneFilter, setPhoneFilter] = useState("");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("");

  const [appliedFilters, setAppliedFilters] = useState<{
    phone?: string;
    partner_id?: string;
    campaign_id?: string;
  }>({});

  const [switchModal, setSwitchModal] = useState<{
    isOpen: boolean;
    exchange: VoucherExchange | null;
  }>({ isOpen: false, exchange: null });
  const [newPartnerId, setNewPartnerId] = useState("");
  const [switchReason, setSwitchReason] = useState("");
  const [switchError, setSwitchError] = useState("");
  const [isSwitching, setIsSwitching] = useState(false);

  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  const showToast = (
    message: string,
    type: "success" | "error" = "success"
  ) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const campaignNameById = Object.fromEntries(
    campaigns.map((c) => [c._id || "", c.name])
  );

  const voucherCampaigns = campaigns.filter(
    (c) => (c.type || "normal") === "voucher"
  );

  const activePartnersForSwitch = partners.filter(
    (p) =>
      p.status === "active" &&
      p._id !== switchModal.exchange?.partner_id
  );

  const fetchLookups = useCallback(async () => {
    try {
      const [campaignRes, partnerRes] = await Promise.all([
        apiService.getCampaigns(),
        partnersService.getPartners(),
      ]);
      setCampaigns(campaignRes.data || []);
      setPartners(partnerRes.data || []);
    } catch {
      // Lookups optional for display
    }
  }, []);

  const fetchExchanges = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");
      const response = await voucherExchangesService.getVoucherExchanges(
        appliedFilters
      );
      setExchanges(response.data || []);
    } catch (err: any) {
      setError(err.message || "Không thể tải lịch sử đổi voucher");
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchLookups();
  }, [fetchLookups]);

  useEffect(() => {
    fetchExchanges();
  }, [fetchExchanges]);

  const handleApplyFilters = () => {
    setAppliedFilters({
      phone: phoneFilter.trim() || undefined,
      partner_id: partnerFilter || undefined,
      campaign_id: campaignFilter || undefined,
    });
  };

  const handleClearFilters = () => {
    setPhoneFilter("");
    setPartnerFilter("");
    setCampaignFilter("");
    setAppliedFilters({});
  };

  const formatTime = (iso: string) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("vi-VN");
  };

  const resolveCampaignName = (ex: VoucherExchange) => {
    if (ex.campaign_name) return ex.campaign_name;
    return campaignNameById[ex.campaign_id] || ex.campaign_id || "—";
  };

  const openSwitchModal = (exchange: VoucherExchange) => {
    setSwitchModal({ isOpen: true, exchange });
    setNewPartnerId("");
    setSwitchReason("");
    setSwitchError("");
  };

  const closeSwitchModal = () => {
    if (isSwitching) return;
    setSwitchModal({ isOpen: false, exchange: null });
    setNewPartnerId("");
    setSwitchReason("");
    setSwitchError("");
  };

  const handleSwitchPartner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!switchModal.exchange) return;

    const reason = switchReason.trim();
    if (!newPartnerId) {
      setSwitchError("Vui lòng chọn nhà thuốc mới");
      return;
    }
    if (!reason) {
      setSwitchError("Vui lòng nhập lý do đổi nhà thuốc");
      return;
    }
    if (reason.length > 500) {
      setSwitchError("Lý do không được vượt quá 500 ký tự");
      return;
    }

    try {
      setIsSwitching(true);
      setSwitchError("");
      await voucherExchangesService.switchVoucherPartner(
        switchModal.exchange._id,
        { new_partner_id: newPartnerId, reason }
      );
      setSwitchModal({ isOpen: false, exchange: null });
      setNewPartnerId("");
      setSwitchReason("");
      showToast("Đổi nhà thuốc thành công");
      await fetchExchanges();
    } catch (err: any) {
      setSwitchError(err.message || "Không thể đổi nhà thuốc");
    } finally {
      setIsSwitching(false);
    }
  };

  const colSpan = canSwitchPartner ? 8 : 7;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[160px]">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Số điện thoại
          </label>
          <input
            type="text"
            value={phoneFilter}
            onChange={(e) => setPhoneFilter(e.target.value)}
            placeholder="VD: 09..."
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="min-w-[180px]">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Đối tác
          </label>
          <select
            value={partnerFilter}
            onChange={(e) => setPartnerFilter(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tất cả đối tác</option>
            {partners.map((p) => (
              <option key={p._id} value={p._id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-[200px]">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Chiến dịch
          </label>
          <select
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tất cả chiến dịch</option>
            {(voucherCampaigns.length > 0 ? voucherCampaigns : campaigns).map(
              (c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              )
            )}
          </select>
        </div>
        <button
          type="button"
          onClick={handleApplyFilters}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Lọc
        </button>
        <button
          type="button"
          onClick={handleClearFilters}
          className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Xóa bộ lọc
        </button>
      </div>

      {error && (
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
          <button
            onClick={fetchExchanges}
            className="ml-3 underline text-sm"
          >
            Thử lại
          </button>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Thời gian
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  SĐT
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Người nhận
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Đối tác
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Mã voucher
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Gift codes
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Chiến dịch
                </th>
                {canSwitchPartner && (
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Thao tác
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={colSpan}
                    className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    Đang tải...
                  </td>
                </tr>
              ) : exchanges.length === 0 ? (
                <tr>
                  <td
                    colSpan={colSpan}
                    className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    Chưa có giao dịch đổi voucher nào.
                  </td>
                </tr>
              ) : (
                exchanges.map((ex) => (
                  <tr
                    key={ex._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                      {formatTime(ex.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono">
                      {ex.phone}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {ex.prize_winner}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {ex.partner_name || ex.partner_id}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-blue-600 dark:text-blue-400">
                      {ex.voucher_code || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-xs break-words">
                      {ex.codes}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {resolveCampaignName(ex)}
                    </td>
                    {canSwitchPartner && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {ex.voucher_status === "issued" ? (
                          <button
                            type="button"
                            onClick={() => openSwitchModal(ex)}
                            className="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700"
                          >
                            Đổi nhà thuốc
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {switchModal.isOpen && switchModal.exchange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              Đổi nhà thuốc
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Voucher{" "}
              <span className="font-mono text-gray-800 dark:text-gray-200">
                {switchModal.exchange.voucher_code || "—"}
              </span>{" "}
              · Hiện tại:{" "}
              <span className="font-medium text-gray-800 dark:text-gray-200">
                {switchModal.exchange.partner_name ||
                  switchModal.exchange.partner_id}
              </span>
            </p>
            <form onSubmit={handleSwitchPartner} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nhà thuốc mới
                </label>
                <select
                  value={newPartnerId}
                  onChange={(e) => setNewPartnerId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSwitching}
                >
                  <option value="">Chọn nhà thuốc...</option>
                  {activePartnersForSwitch.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {activePartnersForSwitch.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Không có nhà thuốc hoạt động khác để chuyển.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Lý do <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={switchReason}
                  onChange={(e) => setSwitchReason(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Nhập lý do đổi nhà thuốc..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSwitching}
                />
                <p className="mt-1 text-xs text-gray-400">
                  {switchReason.trim().length}/500
                </p>
              </div>
              {switchError && (
                <div className="text-sm text-red-600 dark:text-red-400">
                  {switchError}
                </div>
              )}
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={closeSwitchModal}
                  disabled={isSwitching}
                  className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={isSwitching || activePartnersForSwitch.length === 0}
                  className="px-4 py-2 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {isSwitching ? "Đang xử lý..." : "Xác nhận đổi"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed top-4 right-4 z-50">
          <div
            className={`px-4 py-3 rounded-md shadow-lg text-white ${
              toast.type === "success" ? "bg-green-500" : "bg-red-500"
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
};

export default VoucherExchangeTab;
